<template>
  <div>
    <PortfolioDialog
      :open="dialogOpen"
      :project="selectedProject"
      @close="dialogOpen = false"
    />

    <section class="section">
      <div class="container">
        <div class="section-header reveal">
          <span class="eyebrow">Portfolio</span>
          <h2 class="heading-lg">Featured Projects</h2>
          <p class="body-lg">
            A selection of enterprise applications built with Vue.js and modern front-end practices.
          </p>
        </div>

        <div class="project-grid">
          <article
            v-for="(project, index) in projects"
            :key="index"
            class="dark-card project-card reveal"
            :class="`reveal-delay-${(index % 3) + 1}`"
            role="button"
            tabindex="0"
            @click="openProject(project)"
            @keydown.enter="openProject(project)"
          >
            <div class="project-card__visual" :data-index="index">
              <span class="project-card__num">{{ String(index + 1).padStart(2, "0") }}</span>
            </div>
            <div class="project-card__body">
              <span class="project-card__category">{{ project.category }}</span>
              <h3 class="heading-md">{{ project.title }}</h3>
              <div class="project-card__tags">
                <span v-for="tech in project.techStack.slice(0, 3)" :key="tech" class="tag">
                  {{ tech }}
                </span>
              </div>
              <span class="project-card__link">
                View Details <v-icon size="16">mdi-arrow-top-right</v-icon>
              </span>
            </div>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from "vue";
import portfolioData from "@/JsonFiles/portfolios.json";
import PortfolioDialog from "./Dialogs/portfolioDialog.vue";
import { useScrollReveal } from "@/composables/useScrollReveal";

useScrollReveal();

const projects = portfolioData;
const dialogOpen = ref(false);
const selectedProject = ref(null);

const openProject = (project) => {
  selectedProject.value = project;
  dialogOpen.value = true;
};
</script>

<style scoped>
.project-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .project-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.project-card {
  overflow: hidden;
  cursor: pointer;
  padding: 0;
}

.project-card:hover .project-card__link {
  color: var(--color-accent);
}

.project-card__visual {
  aspect-ratio: 16 / 10;
  background: var(--color-surface-2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.project-card__visual[data-index="1"] {
  background: #181818;
}

.project-card__visual[data-index="2"] {
  background: #1e1e1e;
}

.project-card__visual::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, transparent, rgba(var(--color-accent-rgb), 0.06));
  opacity: 0;
  transition: opacity var(--transition);
}

.project-card:hover .project-card__visual::after {
  opacity: 1;
}

.project-card__num {
  font-size: 3rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.05);
}

.project-card__body {
  padding: 1.5rem;
}

.project-card__category {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-accent);
  margin-bottom: 0.5rem;
}

.project-card__body .heading-md {
  margin-bottom: 1rem;
}

.project-card__link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-top: 1rem;
  transition: color var(--transition);
}
</style>
